/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import { authClearActivity, authExportActivity, authListActivity, AuthActivityItem } from '../../data';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type SortingState
} from '@tanstack/react-table';

function AuthEventsSettings() {
    const [rows, setRows] = useState<AuthActivityItem[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'created', desc: true }]);
    const [globalFilter, setGlobalFilter] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const rc = await authListActivity(search, 500);
            setRows(rc);
        } catch (e: any) {
            setError(e?.message || 'Failed to load auth events');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const columns = useMemo<ColumnDef<AuthActivityItem>[]>(() => [
        {
            accessorKey: 'created',
            header: 'Created',
            cell: ({ row }) => row.original.created ? new Date(row.original.created * 1000).toLocaleString() : ''
        },
        { accessorKey: 'username', header: 'Username' },
        { accessorKey: 'event', header: 'Event' },
        { accessorKey: 'remote_ip', header: 'Remote IP' },
        {
            accessorKey: 'details',
            header: 'Details',
            cell: ({ row }) => <div style={{ maxWidth: 420, overflowX: 'auto' }}>{row.original.details || ''}</div>
        }
    ], []);

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting, globalFilter },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn: (row, _columnId, filterValue) => {
            const q = String(filterValue || '').toLowerCase();
            if (!q) return true;
            const rec = row.original;
            return String(rec.username || '').toLowerCase().includes(q)
                || String(rec.event || '').toLowerCase().includes(q)
                || String(rec.remote_ip || '').toLowerCase().includes(q)
                || String(rec.details || '').toLowerCase().includes(q);
        }
    });

    const triggerDownload = (fileName: string, mime: string, content: string) => {
        const blob = new Blob([content], { type: mime });
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
    };

    return (
        <div>
            <h4 className="mb-3">Authentication Events</h4>
            {error ? <Alert variant="danger">{error}</Alert> : null}

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Events</span>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-danger"
                            onClick={async () => {
                                if (!window.confirm('Clear all authentication events?')) return;
                                try {
                                    await authClearActivity();
                                    await load();
                                } catch (e: any) {
                                    setError(e?.message || 'Failed clearing events');
                                }
                            }}
                        >
                            Clear All
                        </Button>
                        <Button
                            variant="outline-info"
                            onClick={async () => {
                                try {
                                    const rc = await authExportActivity('json', search);
                                    triggerDownload(rc.fileName, rc.mime, rc.content);
                                } catch (e: any) {
                                    setError(e?.message || 'Failed exporting JSON');
                                }
                            }}
                        >
                            Download JSON
                        </Button>
                        <Button
                            variant="outline-info"
                            onClick={async () => {
                                try {
                                    const rc = await authExportActivity('csv', search);
                                    triggerDownload(rc.fileName, rc.mime, rc.content);
                                } catch (e: any) {
                                    setError(e?.message || 'Failed exporting CSV');
                                }
                            }}
                        >
                            Download CSV
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    <InputGroup className="mb-2">
                        <Form.Control
                            placeholder="Filter events"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    load();
                                }
                            }}
                        />
                        <Button variant="primary" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Filter'}</Button>
                    </InputGroup>

                    <InputGroup className="mb-2">
                        <Form.Control
                            placeholder="Client-side table filter"
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                        />
                    </InputGroup>

                    <Table striped bordered hover size="sm">
                        <thead>
                            {table.getHeaderGroups().map((hg) => (
                                <tr key={hg.id}>
                                    {hg.headers.map((header) => (
                                        <th key={header.id} style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }} onClick={header.column.getToggleSortingHandler()}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getIsSorted() === 'asc' ? ' ▲' : ''}
                                            {header.column.getIsSorted() === 'desc' ? ' ▼' : ''}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
}

export default AuthEventsSettings;
